// style-picker.ts — §PICKER — the Style Picker's behaviour, separated from its markup.
//
// Everything a reviewer needs to trust about this surface is a decision, not a pixel: which packs
// are eligible, what a filter means, whether a preview can touch saved state, and what cancel
// restores. Those live here as pure functions so they can be proven; the component renders them.
//
// THE LOAD-BEARING RULE: preview NEVER mutates. `previewState` holds a candidate, the saved spec is
// untouched until `confirm()`, and `cancel()` throws the candidate away. A picker that writes as you
// browse is how somebody loses the style they had.
import { STYLE_PACKS, makeStyleSpec, resolveStyle, type StylePackEntry, type StyleSpec, type StyleIntensity } from '@vibeshell/shared';

export type ThemeChoice = 'light' | 'dark' | 'auto';
export type Viewport = 'mobile' | 'desktop';

export interface PickerFilters {
  /** matches id, name and voice, case-insensitively */
  search: string;
  voice: string | null;
  /** only packs that declare this theme */
  theme: 'light' | 'dark' | null;
  intensity: StyleIntensity | null;
  /** only packs that can paint every one of these */
  requiredScripts: string[];
  /** hide packs measured over the frame budget in the selected intensity */
  excludeHeavy: boolean;
}

export const emptyFilters = (): PickerFilters => ({ search: '', voice: null, theme: null, intensity: null, requiredScripts: [], excludeHeavy: false });

export interface PackRow {
  pack: StylePackEntry;
  eligible: boolean;
  /** why a pack is not selectable, in words a person can act on */
  ineligibleReason: string | null;
  /** true when this pack+intensity is measured over the frame budget */
  heavy: boolean;
  /** the themes this pack actually declares — the UI must not offer others */
  themes: string[];
}

/** Every pack, annotated. Ineligible packs are RETURNED, not hidden: "why can't I pick that one" is
 *  a question the surface should answer rather than dodge. */
export function pickerRows(filters: PickerFilters, packs: Record<string, StylePackEntry> = STYLE_PACKS): PackRow[] {
  const q = filters.search.trim().toLowerCase();
  const rows: PackRow[] = [];
  for (const pack of Object.values(packs)) {
    const hay = `${pack.id} ${pack.name} ${pack.scriptVoice}`.toLowerCase();
    if (q && !hay.includes(q)) continue;
    if (filters.voice && pack.scriptVoice !== filters.voice) continue;

    const intensity = filters.intensity ?? 'standard';
    const heavy = pack.expressiveOverBudget && intensity === 'expressive';
    let reason: string | null = null;
    const missingScripts = filters.requiredScripts.filter((s) => !pack.scripts.includes(s));
    if (missingScripts.length) reason = `cannot paint ${missingScripts.join(', ')}`;
    else if (filters.theme && !pack.themes.includes(filters.theme)) reason = `has no ${filters.theme} theme`;
    else if (filters.intensity && !pack.intensities.includes(filters.intensity)) reason = `does not offer ${filters.intensity}`;
    else if (filters.excludeHeavy && heavy) reason = 'measured over the frame budget in expressive';

    rows.push({ pack, eligible: reason === null, ineligibleReason: reason, heavy, themes: pack.themes });
  }
  return rows.sort((a, b) => Number(b.eligible) - Number(a.eligible) || a.pack.name.localeCompare(b.pack.name));
}

/** The themes the UI may offer for a pack. Never invent one it does not declare. */
export const availableThemes = (pack: StylePackEntry): ThemeChoice[] => ['auto', ...pack.themes.filter((t) => t === 'light' || t === 'dark')] as ThemeChoice[];
export const availableIntensities = (pack: StylePackEntry): StyleIntensity[] => pack.intensities as StyleIntensity[];

export type PickerStatus = 'idle' | 'loading' | 'error' | 'unsupported';

export interface PickerState {
  status: PickerStatus;
  error: string | null;
  /** the app's SAVED style — the thing cancel returns to */
  saved: StyleSpec | null;
  /** what the user is currently looking at; null means "showing saved" */
  candidate: StyleSpec | null;
  viewport: Viewport;
  filters: PickerFilters;
  /** set after a confirm, so undo has somewhere to go */
  undoTo: StyleSpec | null;
}

export function initPicker(spec: Record<string, unknown> | null): PickerState {
  const resolved = spec ? resolveStyle(spec) : null;
  return {
    status: 'idle', error: null,
    saved: (spec?.style as StyleSpec | undefined) ?? null,
    candidate: null,
    viewport: 'desktop',
    filters: emptyFilters(),
    undoTo: null,
    ...(resolved === null && spec?.style ? { status: 'unsupported' as PickerStatus, error: 'this app carries a style the registry cannot resolve; pick a pack to replace it' } : {}),
  };
}

/** Select a pack for PREVIEW. Returns new state; the saved spec is not involved. */
export function preview(state: PickerState, packId: string, opts: { intensity?: StyleIntensity; themeMode?: ThemeChoice } = {}): PickerState {
  const pack = STYLE_PACKS[packId];
  if (!pack) return { ...state, status: 'error', error: `"${packId}" is not in the registry` };
  const intensity = opts.intensity && pack.intensities.includes(opts.intensity) ? opts.intensity : 'standard';
  const themeMode = opts.themeMode && (opts.themeMode === 'auto' || pack.themes.includes(opts.themeMode)) ? opts.themeMode : 'auto';
  const candidate = makeStyleSpec(packId, { styleIntensity: intensity, themeMode });
  if (!candidate) return { ...state, status: 'error', error: `could not build a style for "${packId}"` };
  return { ...state, status: 'idle', error: null, candidate };
}

/** Throw the candidate away. The app is exactly as it was. */
export const cancel = (state: PickerState): PickerState => ({ ...state, candidate: null, status: 'idle', error: null });

/** Commit the candidate. Records what to undo to. Returns the state AND the spec patch to persist. */
export function confirm(state: PickerState): { state: PickerState; patch: { style: StyleSpec } | null } {
  if (!state.candidate) return { state, patch: null };
  return {
    state: { ...state, saved: state.candidate, undoTo: state.saved, candidate: null, status: 'idle' },
    patch: { style: state.candidate },
  };
}

/** Undo the last confirm. `patch.style === null` means "remove the key", restoring a legacy app. */
export function undo(state: PickerState): { state: PickerState; patch: { style: StyleSpec | null } } {
  return { state: { ...state, saved: state.undoTo, undoTo: null, candidate: null }, patch: { style: state.undoTo } };
}

/** What the preview surface should render right now. */
export const effectiveStyle = (state: PickerState): StyleSpec | null => state.candidate ?? state.saved;

/** A short, honest line about cost for the heavy mode, shown next to the control that selects it. */
export function performanceNote(pack: StylePackEntry, intensity: StyleIntensity): string | null {
  if (!pack.expressiveOverBudget || intensity !== 'expressive') return null;
  return 'Measured over the frame budget on the reference device. It stays available, and the runtime degrades the effect under load or reduced motion.';
}
